import { useEffect, useRef, useState } from 'react';

/**
 * How long the typing has to settle before a request goes out.
 *
 * Long enough that a word typed at speed costs one request rather than six,
 * short enough that the list still feels like it is answering the keyboard.
 */
export const SEARCH_DEBOUNCE_MS = 250;

export type AsyncOptionsStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface AsyncOptionsInput<T> {
  loadOptions: (query: string, signal: AbortSignal) => Promise<T[]>;
  query: string;
  /** Nothing is fetched while the list is off screen. */
  enabled: boolean;
  minQueryLength: number;
  debounceMs: number;
}

export interface AsyncOptionsResult<T> {
  status: AsyncOptionsStatus;
  options: T[];
}

/**
 * The results for what is currently typed, kept honest about which request
 * they came from.
 *
 * Two separate guards, because they answer two different failures. The abort
 * signal lets a loader that honours it stop work nobody is waiting for. The
 * `live` flag ignores whatever comes back anyway — a loader that does not take
 * the signal seriously still resolves, and an early request landing after a
 * later one would put results for "an" under the word "anne".
 *
 * An empty query is not debounced: it is what opening the list asks, and a
 * quarter second of nothing is the first thing the user would see.
 */
export function useAsyncOptions<T>({
  loadOptions,
  query,
  enabled,
  minQueryLength,
  debounceMs,
}: AsyncOptionsInput<T>): AsyncOptionsResult<T> {
  const [result, setResult] = useState<AsyncOptionsResult<T>>({
    status: 'idle',
    options: [],
  });

  /*
   * Callers pass this inline, so it is a new function on every render. Held in
   * a ref, it can stay out of the dependencies — otherwise every render would
   * cancel the request the render before it started, and nothing would ever
   * load.
   */
  const load = useRef(loadOptions);
  useEffect(() => {
    load.current = loadOptions;
  });

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const controller = new AbortController();
    const tooShort = query.length < minQueryLength;
    let live = true;

    const timer = setTimeout(
      () => {
        if (tooShort) {
          setResult({ status: 'idle', options: [] });
          return;
        }

        setResult((previous) => ({ status: 'loading', options: previous.options }));

        load.current(query, controller.signal).then(
          (options) => {
            if (live) {
              setResult({ status: 'ready', options });
            }
          },
          () => {
            if (live) {
              setResult({ status: 'error', options: [] });
            }
          },
        );
      },
      query === '' || tooShort ? 0 : debounceMs,
    );

    return () => {
      live = false;
      controller.abort();
      clearTimeout(timer);
    };
  }, [enabled, query, minQueryLength, debounceMs]);

  return result;
}
